import { Dict } from "./types"

const GENESIS_TOKEN = 0 // 0b00
const GENESIS_IDX_OFFSET = 2

/**
 * @dev Pack a Genesis index into its ID
 */
export function packGenesisId(genesisIdx: number): number {
  return genesisIdx << GENESIS_IDX_OFFSET
}

/**
 * @dev Unpack a Genesis ID to get its index
 */
export function unpackGenesisId(genesisId: number): [number] {
  return [genesisId >> GENESIS_IDX_OFFSET]
}

/**
 * Print token IDs are made up of (left to right):
 * - Up to x bits for print nonce (0-...)
 * - 12 bits for print generation (0-3999)
 * - 6 bits for Genesis index
 * - 2 bits for token type (1)
 */

const PRINT_NONCE_OFFSET = 20

const PRINT_GENERATION_OFFSET = 8
const PRINT_GENERATION_MASK = 4095 // 0b111111111111

const PRINT_GENESIS_IDX_OFFSET = 2
const PRINT_GENESIS_IDX_MASK = 63 // 0b111111

const PRINT_TOKEN = 1 // 0b01

/**
 * @dev Pack print properties into a token ID
 */
export function packPrintId(
  genesisIdx: number,
  printNonce: number,
  printGeneration: number
): number {
  return (
    (printNonce << PRINT_NONCE_OFFSET) +
    (printGeneration << PRINT_GENERATION_OFFSET) +
    (genesisIdx << PRINT_GENESIS_IDX_OFFSET) +
    PRINT_TOKEN
  )
}

/**
 * @dev Unpack a print ID to its components
 */
export function unpackPrintId(printId: number): [number, number, number] {
  const printNonce = printId >> PRINT_NONCE_OFFSET
  const printGeneration =
    (printId >> PRINT_GENERATION_OFFSET) & PRINT_GENERATION_MASK
  const genesisIdx =
    (printId >> PRINT_GENESIS_IDX_OFFSET) & PRINT_GENESIS_IDX_MASK
  return [genesisIdx, printNonce, printGeneration]
}

/**
 * Mutagen token IDs are made up of (left to right):
 * - 2 bits for layer number (0-3)
 * - 7 bits for variant number (0-99)
 * - 12 bits for Mutagen index (0-3999)
 * - 2 bits for token type (2)
 */

const MUTAGEN_LAYER_OFFSET = 21 // 14 bits from variant offset + 7 bits from variant

const MUTAGEN_VARIANT_OFFSET = 14 // 2 bits from index offset + 12 bits from index
const MUTAGEN_VARIANT_MASK = 127 // 7 bits - 0b1111111

const MUTAGEN_IDX_OFFSET = 2 // 2 bits from token type
const MUTAGEN_IDX_MASK = 4095 // 12 bits - 0b111111111111

const MUTAGEN_TOKEN = 2 // 0b10

/**
 * @dev Pack Mutagen properties into a unique ID
 */
export function packMutagenId(
  layer: number,
  variant: number,
  mutagenIdx: number
): number {
  return (
    (layer << MUTAGEN_LAYER_OFFSET) +
    (variant << MUTAGEN_VARIANT_OFFSET) +
    (mutagenIdx << MUTAGEN_IDX_OFFSET) +
    MUTAGEN_TOKEN
  )
}

/**
 * @dev Unpack Mutagen ID into its properties
 */
export function unpackMutagenId(mutagenId: number): [number, number, number] {
  const layer = mutagenId >> MUTAGEN_LAYER_OFFSET
  const variant = (mutagenId >> MUTAGEN_VARIANT_OFFSET) & MUTAGEN_VARIANT_MASK
  const mutagenIdx = (mutagenId >> MUTAGEN_IDX_OFFSET) & MUTAGEN_IDX_MASK
  return [layer, variant, mutagenIdx]
}

export function parseTokenType(id: number): "genesis" | "print" | "mutagen" {
  const tokenTypeBits = id & 0b11

  if (tokenTypeBits == GENESIS_TOKEN) {
    return "genesis"
  } else if (tokenTypeBits == PRINT_TOKEN) {
    return "print"
  } else return "mutagen"
}

export interface Artist {
  id: string;
  name: string;
  description: string;
  instagram: {
    name: string;
    link: string;
  };
}

export const artists: Dict<Artist> = {
  "nik-kosmas": {
    id: "nik-kosmas",
    name: "Nik Kosmas",
    description:
      "Nik Kosmas is an artist based in Shanghai. His work is a physical, emotional, and spiritual journey through technology, human experience, suffering and growth. Science fiction and science, sports practice, sex and psychology are fertile fields of research and inspiration. He has exhibited in spaces like the New Museum, NY, and participated in the 9th Berlin Biennial. Kosmas was formerly half of post-internet duo AIDS-3D. Bodyhunter E2 genesis is created in collaboration with Sam Lubicz. Read more about Mutagen creative team from docs.",
    instagram: {
      name: "@nikkosmas",
      link: "https://www.instagram.com/nikkosmas",
    },
  },
  "tommy-cash": {
    id: "tommy-cash",
    name: "Tommy Cash",
    description:
      "Tommy Cash (sometimes stylised as TOMM¥ €A$H, b. 1991, Tallinn) is an Estonian rapper, singer and conceptual artist. His rich and colorful visual language is regularly spotted in prestigious art venues as well as on the occasion of numerous collaborations of the artist with fashion brands and designers such as Rick Owens, Adidas, or Maison Margiela. Read more about Mutagen creative team from docs.",
    instagram: {
      name: "@tommycashworld",
      link: "https://www.instagram.com/tommycashworld",
    },
  },
  "ilja-karilampi": {
    id: "ilja-karilampi",
    name: "Ilja Karilampi",
    description: `Ilja Karilampi's work explores music and pop culture narratives, creating new effects, journeys, and alternative ways to look at ourselves and the surface. He's restructuring the over-consumption of the day to day advertorial language and creating a vibrant actively reactional hybrid, half way between record covers and new census readings.
        Essentially a storyteller, his output over the last decade has been reflecting his own role as the producer and his aesthetic interests in glamorous social realism. By means of unorthodox graphic design, video effects, laser-cut aluminium, UV stickers and rhythmic musical gestures, Karilampi assembles conceptual and sculptural narratives of urban life.
       Originally from Gothenburg, Sweden, and educated at Frankfurts Städelschule, Karilampi has exhibited at Revolver Galería, Lima (2020); Carl Kostyal, Malmö (2019); Manifesta, Palermo, (2018); Kiasma, Helsinki (2017), La Plage, Paris (2015); and MoMA PS1, New York (2012). Read more about Mutagen creative team from docs.`,
    instagram: {
      name: "@iljakarilampi",
      link: "https://www.instagram.com/iljakarilampi",
    },
  },
  "katja-novitskova": {
    id: "katja-novitskova",
    name: "Katja Novitskova",
    description:
      "Katja Novitskova is an Estonian artist based in Amsterdam and Berlin. Her practice explores the ecologies surrounding image production on a level capable of affecting the planet and humanity. Novitskova’s work features mainly images found online and and interrogates the positions and locations where the technological  and the physical coincide. Blurring distinctions between media, she is interested in the interpretation and perception of visual material through digital collages, sculptures and installations. Novitskova represented Estonia at the 57th Venice Biennale and exhibited in galleries and institutions including Hamburger Bahnhof, Berlin (2019); Kumu Art Museum, Tallinn; Whitechapel Gallery, London (2018); CC Foundation, Shanghai (2017); Greene Naftali, New York (2016); Museum of Modern Art, New York (2015); Kraupa-Tuskany Zeidler (2014). Read more about Mutagen creative team from docs.",
    instagram: {
      name: "@katjanovi",
      link: "https://www.instagram.com/katjanovi",
    },
  }
}
